import { type ItemOf } from '@shared/board'
import { type Material } from '@shared/material'
import { MessageSquarePlus, Scissors } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { NO_DRAG } from '@/board/classes'
import { useBoardCtx } from '@/board/context'
import { newClip } from '@/board/factory'
import { withMembership } from '@shared/whiteboard/membership'
import { useI18n } from '@/i18n/i18n'
import { loadMaterialText, pdfUrl } from './hooks'
import { loadPdf, pdfjs } from './pdf'
import { fractionRect, lineBoxes, linesAround } from './selection'

/** What is selected in the clip's text, in page fractions. */
type Picked = { text: string; focus: ReturnType<typeof fractionRect>[] }

/**
 * The words of a clip, selectable, laid over it while it is selected (the PDF's
 * own text layer, cut to what the clip shows). What is selected can be cut out
 * as a clip of its own, or said something about.
 */
export function ClipText({
  item,
  material,
  width,
  height,
}: {
  item: ItemOf<'clip'>
  material: Material
  width: number
  height: number
}) {
  const { t } = useI18n()
  const { boardId, client, me, actions } = useBoardCtx()
  const layer = useRef<HTMLDivElement>(null)
  const [picked, setPicked] = useState<Picked | null>(null)
  const { page, rect } = item.data

  useEffect(() => {
    const el = layer.current
    if (!el) return
    let gone = false
    let cancel: (() => void) | null = null
    void Promise.all([loadPdf(pdfUrl(boardId, material)), pdfjs()]).then(async ([doc, m]) => {
      const p = await doc.getPage(page)
      if (gone) return
      const base = p.getViewport({ scale: 1 })
      const scale = width / (rect.w * base.width)
      const vp = p.getViewport({ scale })
      el.replaceChildren()
      el.style.width = `${vp.width}px`
      el.style.height = `${vp.height}px`
      el.style.left = `${-rect.x * vp.width}px`
      el.style.top = `${-rect.y * vp.height}px`
      el.style.setProperty('--scale-factor', String(scale))
      const text = new m.TextLayer({
        textContentSource: p.streamTextContent(),
        container: el,
        viewport: vp,
      })
      text.render().catch(() => undefined)
      cancel = () => {
        text.cancel()
      }
    })
    return () => {
      gone = true
      cancel?.()
    }
  }, [boardId, material, page, rect.x, rect.y, rect.w, width])

  const onPointerUp = () => {
    requestAnimationFrame(() => {
      const sel = window.getSelection()
      const el = layer.current
      if (!sel || sel.isCollapsed || !sel.rangeCount || !el) {
        setPicked(null)
        return
      }
      const range = sel.getRangeAt(0)
      if (!el.contains(range.commonAncestorContainer)) return
      // The layer is the whole page, moved so the clip's region shows: fractions of it are page fractions.
      const box = el.getBoundingClientRect()
      const rects = [...range.getClientRects()].filter((r) => r.width > 0 && r.height > 0)
      if (!rects.length) return
      setPicked({
        text: sel.toString(),
        focus: lineBoxes(rects).map((r) => fractionRect(r.left, r.top, r.right, r.bottom, box)),
      })
    })
  }

  const cut = async (p: Picked) => {
    const text = await loadMaterialText(boardId, material)
    const view = linesAround(text?.pages[page - 1] ?? [], p.focus) ?? rect
    const clip = newClip(
      client.view,
      me,
      material,
      page,
      view,
      p.text.replace(/\s+/g, ' ').trim(),
      { x: item.x + item.w + 200, y: item.y + 100 },
      p.focus,
    )
    client.apply(withMembership(client.view, [{ type: 'item.create', item: clip }]))
    window.getSelection()?.removeAllRanges()
    setPicked(null)
  }

  const button =
    'inline-flex h-7 items-center gap-1 rounded-md px-2 text-[12px] font-medium hover:bg-surface-2'
  return (
    <div
      className={NO_DRAG + ' absolute inset-0 overflow-hidden'}
      style={{ width, height }}
      onPointerUp={onPointerUp}
    >
      <div ref={layer} className="textLayer absolute" />
      {picked && (
        <div
          className="absolute bottom-1 left-1 z-10 flex items-center gap-0.5 rounded-lg border border-line bg-surface p-0.5 shadow-lg"
          onPointerUp={(e) => {
            e.stopPropagation()
          }}
        >
          <button type="button" className={button} onClick={() => void cut(picked)}>
            <Scissors size={13} />
            {t('clip.cutAgain')}
          </button>
          <button
            type="button"
            className={button}
            onClick={() => {
              actions.current?.sayAbout({
                items: [item.id],
                quote: picked.text.trim().slice(0, 2000),
              })
              window.getSelection()?.removeAllRanges()
              setPicked(null)
            }}
          >
            <MessageSquarePlus size={13} />
            {t('clip.sayAbout')}
          </button>
        </div>
      )}
    </div>
  )
}
